// Block D20 Part A — frontend mirror of backend lib/taskTime.js (taskInstantUtc).
//
// Tasks store their time as a BARE WALL-CLOCK string (no timezone). "Overdue" must be judged
// by reinterpreting that wall-clock in the user's STORED timezone (currentUser.timezone) — NOT
// the browser's — so the list badge, the Overdue filter and the chat all agree even when the
// browser timezone differs (travel). This computes the same true-UTC instant the backend does.
//
// Where it differs from the backend taskInstantUtc:
//  - It must be GIVEN the stored timezone (the backend reads user.timezone from the DB).
//  - It parses the wall-clock digits via `+ 'Z'` (not `new Date(str)`) precisely so a non-UTC
//    browser doesn't shift the digits — the backend can use `new Date(str)` only because its
//    server clock is UTC.
//  - A date-only value (no time component) is treated as end-of-day 23:59:59; the backend's
//    dateFromTask yields midnight for a date-only *deadline* specifically. This sub-day gap
//    only matters for date-only deadlines (rare — most tasks carry a time).

import { expandRecurringTasks } from './recurrenceUtils';

// A series row the app draws as occurrences (the same test as recurrenceUtils and the backend's
// lib/taskSeries isDrawnSeries): a drawn rule type and a start time.
const DRAWN_TYPES = ['daily', 'weekly', 'monthly', 'weekday', 'yearly', 'annually'];
function isDrawnSeriesRow(task) {
  if (!task?.recurrence || task.isRecurringInstance) return false;
  const raw = task.recurrence.type || task.recurrence.frequency || task.recurrence.rule;
  if (typeof raw !== 'string' || !DRAWN_TYPES.includes(raw.toLowerCase())) return false;
  return !!task.datetime && !Number.isNaN(new Date(task.datetime).getTime());
}

function normalizeUserTimezone(tz) {
  return (!tz || tz === 'UTC') ? 'Europe/London' : tz; // legacy shim, same as the backend
}

// Reinterpret the wall-clock digits of `wallClockDate` as `timezone` → true UTC instant.
function wallClockToUtcDate(wallClockDate, timezone) {
  const s = wallClockDate.toISOString().slice(0, 19);
  const asIfUtc = new Date(s + 'Z');
  const localStr = new Intl.DateTimeFormat('sv', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(asIfUtc).replace(' ', 'T');
  const correction = asIfUtc.getTime() - new Date(localStr + 'Z').getTime();
  return new Date(asIfUtc.getTime() + correction);
}

// True UTC instant for a task's stored wall-clock, in the user's stored timezone.
// Field precedence mirrors the backend dateFromTask: deadline → datetime → date.
export function taskInstant(task, userTimezone) {
  if (!task) return null;
  const tz = normalizeUserTimezone(userTimezone);
  let raw = task.deadline || task.datetime || task.date || null;
  if (!raw) return null;
  let wallClockStr = String(raw).slice(0, 19);
  if (!wallClockStr.includes('T')) wallClockStr += 'T23:59:59'; // date-only → end of day
  const asUtc = new Date(wallClockStr + 'Z'); // '+Z' keeps the raw digits regardless of browser tz
  if (isNaN(asUtc.getTime())) return null;
  return wallClockToUtcDate(asUtc, tz);
}

// CALENDAR BUFFER BLOCKS (Carla's item 5, 2026-10-12). The backend makes them around a meeting
// (lib/calendarBuffers.js: source 'buffer', a datetime and a deadline that is the buffer's end) and
// nobody ever ticks them, so every one read as overdue once the meeting was over. A buffer is never
// overdue and counts as done once its end has passed. Mirrors Aidey Backend/lib/taskTime.js.
export function isBufferBlock(task) {
  return !!task && task.source === 'buffer';
}

export function isBufferDone(task, userTimezone, now = Date.now()) {
  if (!isBufferBlock(task)) return false;
  const end = taskInstant({ deadline: task.deadline, datetime: task.datetime, date: task.date }, userTimezone);
  return !!end && end.getTime() < now;
}

// The single overdue predicate. `deadline || datetime || date` (a task with a time but no
// explicit deadline still becomes overdue once that time passes); a task with no date is never
// overdue (fixes the old `new Date(null)` → 1970 → always-overdue bug).
export function isTaskOverdue(task, userTimezone) {
  if (!task || task.completed) return false;
  if (isBufferBlock(task)) return false;   // a buffer is never overdue (above)
  // A repeating task as stored (one row; the Team dashboard reads these) is judged by its latest
  // occurrence that has ended, not its first: overdue unless that one was ticked done on its own; a
  // skipped one does not count (Carla's decision 2). An occurrence the app drew is judged as itself
  // below. Aidey Backend/ci/task_occurrence_cases.json owns the cases, for both runtimes.
  if (isDrawnSeriesRow(task)) {
    const nowWall = new Date(new Intl.DateTimeFormat('sv', {
      timeZone: normalizeUserTimezone(userTimezone),
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(new Date()).replace(' ', 'T'));
    const due = expandRecurringTasks([task], new Date(0), nowWall).filter((o) => new Date(o.deadline) < nowWall);
    if (!due.length) return false;
    const latest = due.reduce((a, b) => (new Date(b.deadline) >= new Date(a.deadline) ? b : a));
    return !latest.completed;
  }
  const d = taskInstant(task, userTimezone);
  return !!d && d.getTime() < Date.now();
}

// WHOSE TASK IS IT (Carla, 2026-10-11: 27 / 4 / 7 overdue for one person on three screens). A task
// is the responsibility of its ASSIGNEES when it has any, otherwise of the person who created it
// (task.userId). Mirrors Aidey Backend/lib/taskTime.js; both assert ci/task_responsible_cases.json.
export function taskAssigneeIds(task) {
  const team = Array.isArray(task?.assignedTeam) ? task.assignedTeam : [];
  return team.map((a) => (typeof a === 'string' ? a : (a && (a.userId || a.id)) || null)).filter(Boolean).map(String);
}

export function isTaskResponsibility(task, userId) {
  if (!task || !userId) return false;
  const assignees = taskAssigneeIds(task);
  return assignees.length ? assignees.includes(String(userId)) : String(task.userId) === String(userId);
}

export function isTaskOverdueFor(task, userId, userTimezone) {
  return isTaskResponsibility(task, userId) && isTaskOverdue(task, userTimezone);
}

// The Productivity "To Do" count: not done and not overdue (fix(task-dates)). Overdue, To Do and
// Completed now partition the tasks; a task with no deadline is To Do until its time has passed.
export function isTaskToDo(task, userTimezone) {
  return !!task && !task.completed && !isTaskOverdue(task, userTimezone);
}

// The My tasks badge (UI-4b): not done, and due by the end of TODAY in the user's stored
// timezone, so overdue plus due today. Same instant as isTaskOverdue; a task with no date never
// counts.
export function isTaskDueByToday(task, userTimezone, now = new Date()) {
  if (!task || task.completed) return false;
  const d = taskInstant(task, userTimezone);
  if (!d) return false;
  const tz = normalizeUserTimezone(userTimezone);
  const today = new Intl.DateTimeFormat('sv', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  return d.getTime() <= wallClockToUtcDate(new Date(`${today}T23:59:59Z`), tz).getTime();
}

// ── HOW A TASK'S TIME IS SHOWN: the one place (fix(task-time) 2442a34, fix(task-dates)) ──────────
//
// Every screen that shows a task's time calls these, and nothing else formats one
// (src/utils/taskTimeDisplay.test.js enforces it). Each screen had its own copy, and they drifted:
// the Dashboard printed `new Date(null)` as the end time ("04:00 pm – 01:00 am": 1 January 1970
// in a UK browser), the View task screen and the day lists printed a date-only deadline as ending
// at 01:00 (midnight UTC), and the formats varied from screen to screen.
//
// The rules:
//   - A task's own fields are a WALL CLOCK as stored ("2026-10-07T16:00:00", no zone): the digits
//     are shown as they are, the same reading taskInstant uses above. A value WITH a zone (a "Z" or
//     an offset, as calendar events carry) is a real instant and is shown in the browser's zone.
//   - No deadline: no end. A date-only deadline is a DATE, never a time: "· due 9 Oct".
//   - A deadline on another day says which day: "4:00 pm – 9 Oct, 5:30 pm".
//   - Anything that will not parse shows nothing, never "Invalid Date" and never 1970.

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

// A Date for a stored value: a Date, a timestamp string, or a bare "HH:MM". null if unusable.
function toWallClock(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  let d;
  if (/^\d{1,2}:\d{2}/.test(s)) d = new Date(`2000-01-01T${s.padStart(5, '0').slice(0, 5)}:00`);
  else if (!/T\d{2}:\d{2}/.test(s)) return null;                // date-only, or not a timestamp
  else d = HAS_ZONE.test(s) ? new Date(s) : new Date(s.slice(0, 19));
  return isNaN(d.getTime()) ? null : d;
}

// A date-only value ("2026-10-09", or a timestamp's date part) as a local calendar date.
function toCalendarDate(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}
const isDateOnly = (value) => !!value && !(value instanceof Date) && /^\d{4}-\d{2}-\d{2}$/.test(String(value).trim());
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// "4:00 pm". { compact: true } gives the chat's "4pm" / "4:30pm". '' when there is nothing to show.
export function formatTaskClock(value, { compact = false } = {}) {
  const d = toWallClock(value);
  if (!d) return '';
  if (compact) {
    const h = d.getHours(), m = d.getMinutes();
    return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h >= 12 ? 'pm' : 'am'}`;
  }
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

// "9 Oct" ({ year: true }: "9 Oct 2026").
export function formatTaskDay(value, { year = false } = {}) {
  const d = value instanceof Date ? value : (toWallClock(value) || toCalendarDate(value));
  if (!d) return '';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}) });
}

// "Thu 9 Oct, 4:00 pm" ({ long: true }: "Thursday 9 October, 4:00 pm"). '' when it will not parse.
export function formatTaskWhen(value, { long = false } = {}) {
  const d = toWallClock(value);
  if (!d) return '';
  const day = d.toLocaleDateString([], long ? { weekday: 'long', day: 'numeric', month: 'long' } : { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day}, ${formatTaskClock(d)}`;
}

// The start of a task as a Date: datetime, else date + time. null if it has none.
function taskStart(task) {
  return toWallClock(task?.datetime) || (task?.date && task?.time ? toWallClock(`${task.date}T${task.time}`) : null);
}

// "4:00 pm", "4:00 pm – 5:30 pm", "4:00 pm – 9 Oct, 5:30 pm", "4:00 pm · due 9 Oct",
// "Due 9 Oct, 5:30 pm", "Due 9 Oct", or '' for a task with no time at all.
export function formatTaskTimeRange(task) {
  if (!task) return '';
  const start = taskStart(task) || (task.time ? toWallClock(task.time) : null);
  const startText = start ? formatTaskClock(start) : '';
  const deadline = task.deadline;
  if (isDateOnly(deadline) || (deadline && !toWallClock(deadline) && toCalendarDate(deadline))) {
    const day = formatTaskDay(toCalendarDate(deadline));
    return startText ? `${startText} · due ${day}` : `Due ${day}`;
  }
  const end = toWallClock(deadline);
  if (!end) return startText;
  const endText = formatTaskClock(end);
  if (!startText) return `Due ${formatTaskDay(end)}, ${endText}`;
  const startDay = taskStart(task);
  if (startDay && !sameDay(startDay, end)) return `${startText} – ${formatTaskDay(end)}, ${endText}`;
  return endText === startText ? startText : `${startText} – ${endText}`;
}

// The date line of the View task screen: "7 Oct 2026", or "7 Oct 2026 – 9 Oct 2026" when the
// deadline is on another day. '' when the task has no date.
export function formatTaskDateLine(task) {
  if (!task) return '';
  const start = taskStart(task) || toCalendarDate(task.date);
  if (!start) return '';
  const first = formatTaskDay(start, { year: true });
  const endDay = toWallClock(task.deadline) || toCalendarDate(task.deadline);
  return endDay && !sameDay(start, endDay) ? `${first} – ${formatTaskDay(endDay, { year: true })}` : first;
}

// ── THE TASK FORM (fix(task-dates)) ──────────────────────────────────────────────────────────
// What the edit form opens with. It did `new Date(task.deadline)`, so a task with no deadline
// opened with a deadline of 1 January 1970 01:00; the form then said "End date is before the start
// date" and disabled Save, and clearing it hit "Please enter ... deadline": a task with no deadline
// could not be edited at all. No deadline now opens as no deadline.
export function taskFormDates(task) {
  return {
    start: taskStart(task),
    deadline: toWallClock(task?.deadline) || toCalendarDate(task?.deadline),
  };
}

// The form's one validation message, or null. A NEW task still needs a deadline, as it always has;
// an EDIT may leave it empty, and saving keeps it empty.
export function taskFormError({ title, start, end, editing }) {
  if (!title || !start) return editing ? 'Please enter a title and start time.' : 'Please enter a title, start time, and deadline.';
  if (!end && !editing) return 'Please enter a title, start time, and deadline.';
  return null;
}
